import {
  forwardRef,
  useId,
  useState,
  type ChangeEvent,
  type HTMLAttributes,
} from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { Camera, Loader2, UserRound } from 'lucide-react';
import { cn } from '../lib/utils.js';
import { peerFocusRing } from '../lib/focus.js';

/* ===========================================================================
 * Avatar — one person, one picture, one scale.
 *
 * SHAPE AND SCALE. The portal draws the same face at three sizes and two
 * shapes (80px rounded-xl in the hero, 30px circle in the nav, 64px circle on
 * the edit form), so a photo that reads well cropped square is cropped round
 * two screens later. That is a decision, not a detail: pick the crop once and
 * every surface inherits it. `size` is a closed six-step scale and `shape` a
 * two-value choice, both of which the call site names — nothing here accepts a
 * raw pixel number, because the moment it does the scale stops being a scale.
 *
 * FALLBACK. When there is no photo we draw initials over a hue derived from
 * the name (see `avatarHue`). The portal's fallback is the COMPANY LOGO, which
 * means every photoless employee is visually identical — a roster of forty
 * workers renders as forty copies of the same mark, and the picture stops
 * carrying any information at all. A per-name hue is not decoration: it is the
 * only thing distinguishing rows in exactly the case where the photo is
 * missing. It is never the ONLY channel — the initials carry the same fact,
 * and the accessible name carries it in text — so a colour-blind or greyscale
 * reader loses nothing.
 *
 * EDITING. Both reference apps reveal the upload affordance on `onMouseEnter`
 * only. That is invisible on every touch device and unreachable by keyboard —
 * there is no hover event to fire, so on a phone the control simply does not
 * exist. Here the trigger is a PERSISTENT button pinned to the bottom-trailing
 * corner; the hover scrim is an ADDITIONAL cue layered on top of it, never the
 * thing that summons it. The file input is `sr-only`, not `hidden`: it must
 * stay in the focus order for the `<label>` to be reachable by Tab, and
 * `display:none` removes it from the accessibility tree entirely.
 *
 * RTL. The edit trigger is anchored with `end-0` and the ring uses no physical
 * direction, so under `dir="rtl"` the badge moves to the bottom-LEFT with the
 * rest of the layout instead of overlapping the wrong corner.
 * ======================================================================== */

/** The committed size scale. Values are px; `size` names them, never numbers. */
const SIZE_PX = {
  xs: 24,
  sm: 32,
  md: 40,
  lg: 48,
  xl: 64,
  '2xl': 80,
} as const;

export type AvatarSize = keyof typeof SIZE_PX;

const avatarVariants = cva(
  // `relative` anchors the edit trigger; `shrink-0` stops a flex row from
  // squashing a face into an ellipse; `select-none` keeps the initials from
  // being swept into a drag-selection of the surrounding text.
  'relative inline-flex shrink-0 select-none items-center justify-center overflow-hidden',
  {
    variants: {
      size: {
        // The two smallest steps were the last arbitrary type sizes in the
        // system — `text-[9px]` and `text-[11px]`, written before the scale had
        // a step this small. 9px was below every floor the scale documents, and
        // the 11px was the dense step spelled by hand. Both now take
        // `text-dense` (11px), which fits either circle: initials are one or two
        // characters, and 11px in a 24px circle still leaves ~6px of ring.
        xs: 'h-6 w-6 text-dense',
        sm: 'h-8 w-8 text-dense',
        md: 'h-10 w-10 text-copy-sm',
        lg: 'h-12 w-12 text-copy',
        xl: 'h-16 w-16 text-h3',
        '2xl': 'h-20 w-20 text-h2',
      },
      shape: {
        circle: 'rounded-full',
        // "Squircle" = the CONTROL radius (10px), not the card's 12px. The
        // handoff draws the roster avatar at `width: 32px; height: 32px;
        // border-radius: 10px` (Hadir Employees.dc.html:307) — the same corner
        // as the button and the input it shares a row with, which is the
        // alignment that actually shows: a squircle avatar appears in a table
        // cell or a queue card header beside controls, not floating in a card.
        //
        // ONE VALUE FOR SIX SIZES IS A COMPROMISE. The mock scales the corner
        // with the face — the 82px profile hero is drawn at 20px — and a single
        // `shape` cannot say both. 10px is aimed at the repeated case (the sm
        // avatar in three roster/queue lists); the two hero call sites are 2px
        // tighter than they were and further from their own mocked corner.
        squircle: 'rounded-control',
      },
      ring: {
        none: '',
        border: 'ring-1 ring-border',
        // For an avatar drawn on INK (the profile hero). A hairline of the
        // page's own border colour disappears there, so the inverse ring is a
        // translucent white — the portal's `border-[3px] border-white/[.18]`,
        // expressed as a ring so it doesn't change the element's box size.
        //
        // It also re-points the fallback palette. An ink band is ink in BOTH
        // themes (that is the whole point of `--nav-surface`), so the light
        // theme's dark fill would sit at 1.28:1 against it for the blue hues —
        // an invisible avatar on the one screen that features it. Overriding
        // the two custom properties here hands this avatar the dark theme's
        // light fill and dark ink whatever the page is doing.
        inverse:
          'ring-[3px] ring-nav-on-surface/20 [--avatar-fill-l:78%] [--avatar-ink:14_13_23]',
      },
    },
    defaultVariants: { size: 'md', shape: 'circle', ring: 'none' },
  }
);

/**
 * Deterministic hue (0–359) for a name.
 *
 * Ported from the sister app's employee list — the best fallback in either
 * reference, and one no profile screen there actually used. Exported because
 * the STABILITY is the contract: the same name must produce the same colour on
 * every screen, in every session, on every machine. A test pins that, because
 * a "random pleasant colour" implementation looks identical in review and
 * quietly reshuffles the roster on each render.
 *
 * FNV-ish rolling hash over UTF-16 code units, forced unsigned so the shift
 * never produces a negative modulus. Case- and whitespace-normalised so
 * "Ali Hassan", "ali hassan" and " Ali  Hassan " are one person.
 */
export function avatarHue(name: string | null | undefined): number {
  const key = (name ?? '').trim().replace(/\s+/g, ' ').toLowerCase();
  let hash = 0;
  for (let i = 0; i < key.length; i += 1) {
    hash = (hash * 31 + key.charCodeAt(i)) >>> 0;
  }
  return hash % 360;
}

/**
 * Up to two initials. Takes the first and LAST word so "Mohammed Abdul Rahman"
 * reads "MR" rather than "MA" — the family name is the discriminator in a
 * roster where a hundred people share a given name. Non-Latin scripts work
 * unchanged: we slice code points, not bytes.
 */
export function avatarInitials(name: string | null | undefined): string {
  const words = (name ?? '').trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return '';
  const first = [...words[0]!][0] ?? '';
  const last = words.length > 1 ? ([...words[words.length - 1]!][0] ?? '') : '';
  return (first + last).toUpperCase();
}

export interface AvatarProps
  extends Omit<HTMLAttributes<HTMLSpanElement>, 'onSelect'>,
    VariantProps<typeof avatarVariants> {
  /** Person's full name — drives the initials, the hue and the alt text. */
  name?: string | null;
  /** Image URL (or object URL). Absent/failed → the initials fallback. */
  src?: string | null;
  /** Renders the persistent upload trigger. Requires `onSelect` to do anything. */
  editable?: boolean;
  /** Shows the busy spinner and blocks further picks. Sets `aria-busy`. */
  uploading?: boolean;
  /** Greys the trigger out and stops it being focusable. */
  disabled?: boolean;
  /** Accessible label for the upload trigger. */
  /** The edit control's accessible name. Only rendered when `editable`, and
   *  both call sites that set that pass a translated one. */
  editLabel?: string;
  /** Announced (visually hidden) while `uploading` is true. */
  uploadingLabel?: string;
  /** Fired with the picked file. The input is reset so re-picking the same
   *  file fires again — otherwise a failed upload can't be retried. */
  onSelect?: (file: File) => void;
  /** `brand`: initials on the tenant's primary, as the phone's profile card draws them. */
  tone?: 'name' | 'brand';
  /** The picture did not decode — the caller may be captioning a photo that is not shown. */
  onImageError?: () => void;
}

export const Avatar = forwardRef<HTMLSpanElement, AvatarProps>(function Avatar(
  {
    className,
    name,
    src,
    size = 'md',
    shape = 'circle',
    ring = 'none',
    editable = false,
    uploading = false,
    disabled = false,
    editLabel = 'Change photo',
    uploadingLabel = 'Uploading photo…',
    onSelect,
    tone = 'name',
    onImageError,
    ...props
  },
  ref
) {
  // A broken URL must fall back to the initials, not to a broken-image glyph.
  // Keyed on `src` so swapping the photo re-arms the check.
  const [failedSrc, setFailedSrc] = useState<string | null>(null);
  const inputId = useId();

  const showImage = !!src && failedSrc !== src;
  const initials = avatarInitials(name);
  const hue = avatarHue(name);
  // A corner badge only fits once the face is at least 40px across.
  const isBadge = SIZE_PX[size ?? 'md'] >= SIZE_PX.md;

  function handleChange(e: ChangeEvent<HTMLInputElement>): void {
    const file = e.target.files?.[0];
    // Clearing the input is what lets the SAME file be picked twice. Without
    // it the second `change` never fires and a retry after a failed upload
    // silently does nothing.
    e.target.value = '';
    if (file && onSelect) onSelect(file);
  }

  return (
    <span
      ref={ref}
      // `overflow-hidden` on the frame would clip the edit badge, so the frame
      // is a plain positioning box and the CLIP lives on the inner face.
      // `group/avatar` is named so a page wrapping this in its own `group`
      // (a linked table row, say) can't accidentally drive the scrim.
      className={cn('group/avatar relative inline-flex shrink-0', className)}
      // Machine-readable half of the uploading state. The visible half is the
      // veil below; neither is sufficient alone.
      aria-busy={uploading || undefined}
      {...props}
    >
      {uploading && (
        <span role="status" className="sr-only">
          {uploadingLabel}
        </span>
      )}
      <span
        className={cn(avatarVariants({ size, shape, ring }), 'font-semibold', !showImage && tone === 'brand' && 'bg-primary text-primary-foreground')}
        /* THE HUE IS GENERATED; THE LIGHTNESS AND THE INK ARE TOKENS.
         *
         * The sister app hard-codes `hsl(<hue> 55% 45%)` with white text, and
         * that pairing is not accessible: swept across all 360 hues it bottoms
         * out at 2.26:1 near yellow — half the 4.5:1 the initials need. Nor can
         * one fixed lightness serve both themes, because a fill dark enough for
         * white ink (4.72:1 at 30%) drops to 1.22:1 against a dark card and
         * disappears.
         *
         * So only the HUE comes from the name. Lightness, saturation and the
         * ink come from `--avatar-fill-*` / `--avatar-ink`, which the app flips
         * per theme exactly like every other colour decision. Fallbacks are
         * inlined so the primitive still renders correctly in an app that has
         * not declared them. Measured: 4.72:1 (worst hue) in light, 8.30:1 in
         * dark. */
        style={
          showImage || tone === 'brand'
            ? undefined
            : {
                backgroundColor: `hsl(${hue} var(--avatar-fill-s, 55%) var(--avatar-fill-l, 30%))`,
                color: 'rgb(var(--avatar-ink, 255 255 255))',
              }
        }
        // Decorative: every call site in this app renders the person's name
        // next to their picture, so announcing it a second time is noise. The
        // upload trigger below is NOT hidden — that one is a real control and
        // carries its own label.
        aria-hidden
      >
        {showImage ? (
          <img
            src={src ?? undefined}
            alt=""
            className="aspect-square h-full w-full object-cover"
            onError={() => {
              setFailedSrc(src ?? null);
              onImageError?.();
            }}
          />
        ) : initials ? (
          initials
        ) : (
          <UserRound className="h-1/2 w-1/2" aria-hidden />
        )}

        {/* Busy veil. `aria-busy` above is the machine-readable half; this is
         *  the visible one, and it must not depend on hover either. */}
        {uploading && (
          <span className="absolute inset-0 grid place-items-center bg-scrim/60">
            <Loader2 className="h-1/3 w-1/3 animate-spin text-white" aria-hidden />
          </span>
        )}

        {/* Hover scrim — an EXTRA cue on top of the always-present trigger,
         *  never the way in. Only for the badge sizes; the smaller ones
         *  already wear a permanent scrim as their target.
         *  `pointer-events-none` so it never eats the click. */}
        {editable && isBadge && !uploading && !disabled && (
          <span className="pointer-events-none absolute inset-0 bg-scrim/0 transition-colors duration-fast ease-standard group-hover/avatar:bg-scrim/35" />
        )}
      </span>

      {editable && (
        <>
          {/* The input comes FIRST because `peer-*` compiles to the general
           *  sibling combinator (`~`), which only reaches FORWARD. With the
           *  label written first the focus ring below would never fire — and
           *  a keyboard focus you cannot see is the same failure as a hover
           *  affordance you cannot reach.
           *
           *  `sr-only`, NEVER `hidden`: a hidden input cannot take focus, and
           *  a label pointing at an unfocusable input is not keyboard-
           *  reachable. This one Tabs like any control and opens the picker on
           *  Enter or Space. */}
          <input
            id={inputId}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            className="peer sr-only"
            disabled={disabled || uploading}
            aria-label={editLabel}
            onChange={handleChange}
          />
          <label
            htmlFor={inputId}
            className={cn(
              'grid place-items-center transition-colors duration-fast ease-standard',
              isBadge
                ? [
                    // Bottom-TRAILING corner (`end-0`), so it flips with `dir`.
                    'absolute -bottom-1 end-0 h-7 w-7 rounded-full',
                    'border-2 border-surface bg-primary text-primary-foreground shadow-resting',
                  ]
                : [
                    // Below 40px a corner badge would be larger than the face
                    // it sits on. The whole avatar becomes the target instead —
                    // still PERSISTENT (a permanent scrim, not a hover one), so
                    // the affordance exists on touch and in a screenshot.
                    'absolute inset-0 rounded-[inherit] bg-scrim/50 text-white',
                    // Restates the face's own corner — it must track `shape`
                    // exactly, or the scrim's edge shows past the face it
                    // covers.
                    shape === 'circle' ? 'rounded-full' : 'rounded-control',
                  ],
              disabled || uploading
                ? 'cursor-not-allowed opacity-50'
                : isBadge
                  ? 'cursor-pointer hover:bg-primary/90'
                  : 'cursor-pointer hover:bg-scrim/65',
              // The input is `sr-only`, so the visible focus ring has to be
              // driven by the input's own focus — `peer` keeps that in CSS,
              // with no JS focus state to fall out of sync.
              peerFocusRing
            )}
          >
            <Camera className={isBadge ? 'h-4 w-4' : 'h-1/2 w-1/2'} aria-hidden />
            <span className="sr-only">{editLabel}</span>
          </label>
        </>
      )}
    </span>
  );
});
